import { create } from 'zustand';
import type { User } from 'firebase/auth';

import type { Usuario } from '@/types/domain';
import { getUserDoc, onAuthChange, signIn as fbSignIn, signOut as fbSignOut } from '@/services/firebase/auth';

interface AuthState {
  initialized: boolean;
  loading: boolean;
  firebaseUser: User | null;
  perfil: Usuario | null;
  error: string | null;
  init: () => () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshPerfil: () => Promise<void>;
}

export const useAuth = create<AuthState>((set, get) => ({
  initialized: false,
  loading: false,
  firebaseUser: null,
  perfil: null,
  error: null,

  init: () => {
    const unsub = onAuthChange(async (user) => {
      if (!user) {
        set({ firebaseUser: null, perfil: null, initialized: true, loading: false });
        return;
      }
      const perfil = await getUserDoc(user.uid);
      set({ firebaseUser: user, perfil, initialized: true, loading: false });
    });
    return unsub;
  },

  signIn: async (email, password) => {
    set({ loading: true, error: null });
    try {
      const user = await fbSignIn(email, password);
      const perfil = await getUserDoc(user.uid);
      if (!perfil) {
        throw new Error('Tu cuenta aún no tiene perfil asignado. Pedile al administrador que la active.');
      }
      if (!perfil.activo) {
        throw new Error('Tu cuenta está inactiva. Contactate con el administrador.');
      }
      set({ firebaseUser: user, perfil, loading: false });
    } catch (e: any) {
      const code: string = e?.code ?? '';
      const map: Record<string, string> = {
        'auth/invalid-credential': 'Email o contraseña incorrectos.',
        'auth/invalid-email': 'El email no es válido.',
        'auth/user-disabled': 'Tu cuenta fue deshabilitada.',
        'auth/too-many-requests': 'Demasiados intentos. Esperá unos minutos.',
        'auth/network-request-failed': 'Sin conexión a internet.',
      };
      set({ loading: false, error: map[code] ?? e?.message ?? 'No se pudo iniciar sesión.' });
      throw e;
    }
  },

  signOut: async () => {
    await fbSignOut();
    set({ firebaseUser: null, perfil: null });
  },

  refreshPerfil: async () => {
    const u = get().firebaseUser;
    if (!u) return;
    const perfil = await getUserDoc(u.uid);
    set({ perfil });
  },
}));

export const selectRol = (s: AuthState) => s.perfil?.rol;
export const selectIsAdmin = (s: AuthState) => s.perfil?.rol === 'admin';
export const selectIsDoctor = (s: AuthState) => s.perfil?.rol === 'doctor';
export const selectIsRecepcion = (s: AuthState) => s.perfil?.rol === 'recepcion';
